import React, { useEffect, useState } from 'react';
import { contentApi } from '../../services/contentApi';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import { CatalogSettings } from '../../types/content';
import ActionStatus from '../../components/UI/ActionStatus';
import { useAutoClear } from '../../hooks/useAutoClear';

const CatalogSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<CatalogSettings | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useAutoClear(message, setMessage, 5000);
  useAutoClear(error, setError, 10000);

  useEffect(() => {
    contentApi.getCatalogSettings()
      .then((r) => {
        const loaded = r.data.settings || null;
        if (!loaded) {
          setSettings(null);
          return;
        }
        setSettings(loaded);
      })
      .catch(() => setError('Could not load catalog settings.'));
  }, []);

  if (!settings) {
    return <p className="text-sm text-gray-600">{error || 'Loading settings…'}</p>;
  }

  const packagesMax = settings.maxCourseInterviewSessions ?? 3;

  return (
    <Card className="p-6 space-y-4 max-w-2xl">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">General catalog configuration</h2>
        <p className="mt-1 text-sm text-gray-600">Owner-only. Controls seat capacity, interview pricing, and course review-session packages.</p>
      </div>
      <Input
        id="max-seats"
        type="number"
        label="Default max seats (self-paced courses)"
        value={String(settings.defaultMaxSeats)}
        onChange={(e) => setSettings({ ...settings, defaultMaxSeats: Number(e.target.value) })}
      />
      <label className="inline-flex items-center gap-2 text-sm text-gray-800">
        <input
          type="checkbox"
          checked={settings.enableInterviewsForCourseEnrollments}
          onChange={(e) => setSettings({ ...settings, enableInterviewsForCourseEnrollments: e.target.checked })}
        />
        Enable interviews for course enrollments
      </label>
      {settings.enableInterviewsForCourseEnrollments && (
        <label className="text-sm text-gray-700 block max-w-xs">
          Max review sessions offered per course (1–3)
          <select
            className="mt-1 w-full rounded-md border-gray-300"
            value={String(settings.maxCourseInterviewSessions ?? 3)}
            onChange={(e) => setSettings({ ...settings, maxCourseInterviewSessions: Number(e.target.value) })}
          >
            <option value={1}>1 session</option>
            <option value={2}>Up to 2 sessions</option>
            <option value={3}>Up to 3 sessions</option>
          </select>
        </label>
      )}
      <p className="text-xs text-gray-500">When disabled, learners can only choose self-paced. When enabled, only packages up to the max above are shown.</p>
      <div className="grid md:grid-cols-3 gap-4">
        <Input
          id="pkg1"
          type="number"
          label="1-session package (INR)"
          value={String(settings.reviewPackage1Price)}
          disabled={packagesMax < 1}
          onChange={(e) => setSettings({ ...settings, reviewPackage1Price: Number(e.target.value) })}
        />
        <Input
          id="pkg2"
          type="number"
          label="2-session package (INR)"
          value={String(settings.reviewPackage2Price)}
          disabled={packagesMax < 2}
          onChange={(e) => setSettings({ ...settings, reviewPackage2Price: Number(e.target.value) })}
        />
        <Input
          id="pkg3"
          type="number"
          label="3-session package (INR)"
          value={String(settings.reviewPackage3Price)}
          disabled={packagesMax < 3}
          onChange={(e) => setSettings({ ...settings, reviewPackage3Price: Number(e.target.value) })}
        />
      </div>
      <div className="border-t border-gray-100 pt-4 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Standalone technical interviews (not course packages)
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <Input
            id="iv-price"
            type="number"
            label="Technical interview list price (INR)"
            value={String(settings.interviewDefaultPrice)}
            onChange={(e) => setSettings({ ...settings, interviewDefaultPrice: Number(e.target.value) })}
          />
          <Input
            id="iv-disc"
            type="number"
            label="Technical interview discount %"
            value={String(settings.interviewDiscountPercent)}
            onChange={(e) => setSettings({ ...settings, interviewDiscountPercent: Number(e.target.value) })}
          />
        </div>
      </div>
      <Button
        disabled={saving}
        onClick={async () => {
          setSaving(true);
          setMessage(null);
          setError(null);
          const payload: CatalogSettings = { ...settings, trialMonetizationEnabled: true };
          try {
            const response = await contentApi.updateCatalogSettings(payload);
            if (!response.data.success) {
              setError(response.data.message || 'Save failed.');
              return;
            }
            setSettings(response.data.settings || payload);
            setMessage(response.data.message || 'Saved.');
          } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Save failed.');
          } finally {
            setSaving(false);
          }
        }}
      >
        {saving ? 'Saving…' : 'Save settings'}
      </Button>
      <ActionStatus message={message} error={error} />
    </Card>
  );
};

export default CatalogSettingsPage;
